import type { $Fetch } from 'ofetch';
import type { CohortGateway } from '@/application/port/out/CohortGateway';
import type {
  CreateCohortCommand,
  ListCohortsQuery,
  UpdateCohortCommand,
} from '@/application/port/in/dto/CohortCommand';
import type { Cohort } from '@/domain/model/Cohort';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse, SpringPage } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

const COHORTS_PATH = '/api/v1/cohorts';

export class HttpCohortGateway implements CohortGateway {
  constructor(private readonly client: $Fetch) {}

  async list(query: ListCohortsQuery): Promise<Page<Cohort>> {
    const params: Record<string, string | number> = {};
    if (query.courseId) params['courseId'] = query.courseId;
    if (typeof query.page === 'number') params['page'] = query.page;
    if (typeof query.size === 'number') params['size'] = query.size;
    if (query.sort) params['sort'] = query.sort;

    const res = await this.client<ApiResponse<SpringPage<Cohort>>>(COHORTS_PATH, {
      method: 'GET',
      params,
    });
    assertApiSuccess(res);
    return this.toDomainPage(res.data);
  }

  async getById(id: string): Promise<Cohort> {
    const res = await this.client<ApiResponse<Cohort>>(
      `${COHORTS_PATH}/${encodeURIComponent(id)}`,
      { method: 'GET' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async create(command: CreateCohortCommand): Promise<Cohort> {
    const res = await this.client<ApiResponse<Cohort>>(COHORTS_PATH, {
      method: 'POST',
      body: command,
    });
    assertApiSuccess(res);
    return res.data;
  }

  async update(id: string, command: UpdateCohortCommand): Promise<Cohort> {
    const res = await this.client<ApiResponse<Cohort>>(
      `${COHORTS_PATH}/${encodeURIComponent(id)}`,
      { method: 'PUT', body: command },
    );
    assertApiSuccess(res);
    return res.data;
  }

  private toDomainPage(p: SpringPage<Cohort>): Page<Cohort> {
    return {
      content: p.content,
      number: p.number,
      size: p.size,
      totalElements: p.totalElements,
      totalPages: p.totalPages,
      first: p.first,
      last: p.last,
    };
  }
}
